import React from 'react';

import type { JsonLdDocument } from '@/lib/seo/structured-data';
import type { SeoMetadata } from '@/lib/seo/metadata';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonicalLink(href: string): void {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

const STRUCTURED_DATA_SCRIPT_ID = 'public-site-structured-data';

function upsertStructuredData(documents: JsonLdDocument[]): void {
  let script = document.getElementById(STRUCTURED_DATA_SCRIPT_ID) as HTMLScriptElement | null;
  if (documents.length === 0) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.id = STRUCTURED_DATA_SCRIPT_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(documents.length === 1 ? documents[0] : documents);
}

// Public unit sites are a pure client-side render with no SSR/prerendering,
// so this is the only place seo_title/seo_description ever reach the
// document — updates existing tags in place (index.html's own static
// marketing-site tags included) rather than appending duplicates, since two
// og:title tags on one page is ambiguous to crawlers. Deliberately does not
// go through usePageTitle()/appConfig.name: a unit's own public site is the
// one surface in this app that represents someone else's brand, not the
// Scouting Ops Site product itself, so its title/description must not carry
// "| Scouting Ops Site" the way every other routed page's does.
export function usePublicSiteSeo(metadata: SeoMetadata | null, structuredData: JsonLdDocument[]): void {
  React.useEffect(() => {
    if (!metadata) return;

    document.title = metadata.title;
    upsertMeta('property', 'og:title', metadata.title);
    upsertMeta('name', 'twitter:title', metadata.title);

    if (metadata.description) {
      upsertMeta('name', 'description', metadata.description);
      upsertMeta('property', 'og:description', metadata.description);
      upsertMeta('name', 'twitter:description', metadata.description);
    } else {
      removeMeta('name', 'description');
      removeMeta('property', 'og:description');
      removeMeta('name', 'twitter:description');
    }

    upsertCanonicalLink(metadata.canonicalUrl);
    upsertMeta('property', 'og:url', metadata.canonicalUrl);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('name', 'robots', metadata.robots);

    if (metadata.ogImage) {
      upsertMeta('property', 'og:image', metadata.ogImage);
      upsertMeta('name', 'twitter:image', metadata.ogImage);
      upsertMeta('name', 'twitter:card', 'summary_large_image');
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
      upsertMeta('name', 'twitter:card', 'summary');
    }

    upsertStructuredData(structuredData);
  }, [metadata, structuredData]);
}
